/**
 * Kitchen feature access is determined by the BOH (back-of-house) department
 * category, not by a hardcoded department name. Stores can rename their kitchen
 * departments (e.g. "Prep", "Breading Side") and keep access as long as those
 * departments are tagged BOH. `departmentCategories` is computed server-side and
 * attached to the user payload.
 *
 * The legacy `departments.includes('Kitchen')` check is kept as a fallback so
 * default-named stores and any session issued before departmentCategories existed
 * continue to work until the next session refresh.
 *
 * Spec: docs/superpowers/specs/2026-06-15-kitchen-access-by-department-category-design.md
 */
type DepartmentBearer = {
  departments?: string[];
  departmentCategories?: string[];
} | null | undefined;

export function hasKitchenAccess(user: DepartmentBearer): boolean {
  if (user?.departmentCategories?.includes('BOH')) return true;
  return Boolean(user?.departments?.includes('Kitchen'));
}

// Default department names by category. Card/action definitions reference these
// default names in their `requiresDepartment` lists; mapping them to a category
// lets a store that renamed its departments (but kept the FOH/BOH tag) still
// match. Food Truck / Everything are category 'none' and rely on literal-name
// matching only.
const DEFAULT_FOH_DEPARTMENTS = ['Front Counter', 'Drive Thru'];
const DEFAULT_BOH_DEPARTMENTS = ['Kitchen', 'Prep'];

/**
 * Whether a user satisfies a content item's `requiresDepartment` list.
 * Matches by literal department name first (covers exact names incl. "Everything"),
 * then by category: a requirement naming a default BOH/FOH department is satisfied
 * by any user belonging to a department of that category (handles renamed stores).
 */
export function userMeetsDepartmentRequirement(
  user: DepartmentBearer,
  requiredDepartments: string[] | null | undefined
): boolean {
  if (!requiredDepartments || requiredDepartments.length === 0) return true;
  const userDepartments = user?.departments || [];
  if (requiredDepartments.some(dept => userDepartments.includes(dept))) return true;

  const categories = user?.departmentCategories || [];
  if (categories.includes('BOH') && requiredDepartments.some(d => DEFAULT_BOH_DEPARTMENTS.includes(d))) return true;
  if (categories.includes('FOH') && requiredDepartments.some(d => DEFAULT_FOH_DEPARTMENTS.includes(d))) return true;
  return false;
}
